import * as React from 'react';
import { useMemo } from 'react';
import { Input } from '../Input/';
import { Combobox } from '../Combobox';
import { DropDownItem } from '../Combobox/';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatPhoneNumber } from './util';
import { countries } from './countries';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { PhoneInputProps } from './types';

export const PhoneInput = React.forwardRef<HTMLButtonElement, PhoneInputProps>(
  (
    {
      inputProps,
      defaultValue,
      onChange,
      value,
      ariaLabelNoCountry,
      error = false,
      ...props
    },
    ref,
  ) => {
    // Sort items by length of value to ensure that the longest matches are selected first, when later
    // performing a search with startsWith()
    const sortedItems = useMemo(
      () => [...countries].sort((a, b) => b.value.length - a.value.length),
      [],
    );

    // Combine phone code and ISO 2 country code into a single value for selecting a correct flag
    // because some countries share the same phone code
    const items = useMemo(
      () =>
        countries.map((country) => ({
          ...country,
          value: `${country.value}|${country?.data?.iso2}`,
        })),
      [],
    );

    const [selected, setSelected] = React.useState<DropDownItem | null>(null);
    const [internalValue, setInternalValue] = React.useState<
      string | undefined
    >(defaultValue ? formatPhoneNumber(defaultValue, sortedItems) : undefined);
    const isControlled = value !== undefined;
    const displayValue = isControlled
      ? formatPhoneNumber(value, sortedItems)
      : internalValue;

    React.useEffect(() => {
      if (!displayValue) {
        setSelected(null);
        return;
      }

      const term = displayValue.replace(/ /g, '');
      const phoneCode = selected?.value.split('|')[0];
      if (phoneCode && term.startsWith(phoneCode)) {
        return;
      }

      const item =
        sortedItems.find((sortedItem) => term.startsWith(sortedItem.value)) ||
        null;
      setSelected(item);
    }, [displayValue, selected?.value, sortedItems]);

    // textfield input
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const phone = formatPhoneNumber(e.target.value, sortedItems);
      if (!isControlled) {
        setInternalValue(phone);
      }
      onChange?.(e);
    };

    // country dropdown
    const handleSelect = (value: string) => {
      const [phoneCode, iso2] = value.split('|');

      if (!isControlled) {
        setInternalValue(phoneCode);
      }
      phoneRef.current?.focus();

      const item =
        items.find((sortedItem) => sortedItem.data?.iso2 === iso2) || null;
      setSelected(item);
      props.onSelect?.(phoneCode);
      onChange?.({
        target: { value: phoneCode },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const flagClassName = 'eye:border eye:border-grey-100';
    const phoneRef = React.useRef<HTMLInputElement>(null);
    const id = React.useId();

    return (
      <div className={'eye:relative'}>
        <Combobox
          className={'eye:min-w-[290px]'}
          hideRenderedValue={true}
          {...props}
          ref={ref}
          items={items}
          error={error}
          itemRenderer={(item) => (
            <>
              <img
                className={cn(flagClassName, 'eye:mr-8')}
                alt=''
                src={`https://flagcdn.com/h20/${item.data?.iso2}.png`}
                height='20'
                width='20'
                loading='lazy'
              />
              {item.label}
            </>
          )}
          labelProps={{
            htmlFor: id,
            renderAsSpan: false,
            label: props.label,
            float: 'top',
            leftSlot: (
              <>
                {selected?.data?.iso2 ? (
                  <img
                    className={flagClassName}
                    alt=''
                    src={`https://flagcdn.com/h20/${selected.data.iso2}.png`}
                    height='20'
                    width='20'
                    loading='lazy'
                  />
                ) : (
                  <IconAlertTriangle
                    aria-label={ariaLabelNoCountry}
                    className={'eye:text-red-100'}
                  />
                )}
                <ChevronDown
                  className={'eye:cursor-pointer'}
                  aria-hidden='true'
                />
              </>
            ),
          }}
          value={selected?.value}
          onSelect={handleSelect}
        />
        <Input
          id={id}
          maxLength={20}
          className={
            'eye:absolute eye:top-[22px] eye:right-[30px] eye:left-[55px] eye:w-auto eye:grow-0'
          }
          {...inputProps}
          required={props.required}
          onClick={(e) => e.stopPropagation()}
          disabled={props.disabled}
          inputRef={phoneRef}
          value={displayValue}
          onChange={handleChange}
        />
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
